import { createContext, useContext, useState, type ReactNode } from "react";
import type { OpportunityStage } from "./opportunityTypes";

export interface StageConfig {
  id: string;
  name: string;
  type: "active" | "won" | "lost";
  order: number;
  archived: boolean;
  color: string;
}

export interface PipelineConfig {
  id: string;
  name: string;
  isDefault: boolean;
  stages: StageConfig[];
}

const DEFAULT_STAGES: StageConfig[] = [
  { id: "s1", name: "Lead", type: "active", order: 0, archived: false, color: "bg-info/15 text-info border-info/30" },
  { id: "s2", name: "Appointment Scheduled", type: "active", order: 1, archived: false, color: "bg-warning/15 text-warning border-warning/30" },
  { id: "s3", name: "Appointment Completed", type: "active", order: 2, archived: false, color: "bg-primary/15 text-primary border-primary/30" },
  { id: "s4", name: "Follow-Up Needed", type: "active", order: 3, archived: false, color: "bg-warning/15 text-warning border-warning/30" },
  { id: "s5", name: "Closed Won", type: "won", order: 4, archived: false, color: "bg-success/15 text-success border-success/30" },
  { id: "s6", name: "Closed Lost", type: "lost", order: 5, archived: false, color: "bg-muted/15 text-muted border-muted/30" },
];

const DEFAULT_PIPELINE: PipelineConfig = {
  id: "pipeline-default",
  name: "Primary Sales Pipeline",
  isDefault: true,
  stages: DEFAULT_STAGES,
};

interface PipelineConfigContextType {
  pipelines: PipelineConfig[];
  activePipeline: PipelineConfig;
  setActivePipelineId: (id: string) => void;
  addStage: (name: string, type: StageConfig["type"]) => void;
  renameStage: (stageId: string, newName: string) => void;
  reorderStages: (stageIds: string[]) => void;
  archiveStage: (stageId: string) => void;
  restoreStage: (stageId: string) => void;
  getVisibleStages: () => StageConfig[];
  getStageColor: (stageName: string) => string;
}

const PipelineConfigContext = createContext<PipelineConfigContextType | null>(null);

const STAGE_COLOR_POOL = [
  "bg-destructive/15 text-destructive border-destructive/30",
  "bg-success/15 text-success border-success/30",
  "bg-warning/15 text-warning border-warning/30",
  "bg-info/15 text-info border-info/30",
  "bg-primary/15 text-primary border-primary/30",
  "bg-success/15 text-success border-success/30",
];

export function PipelineConfigProvider({ children }: { children: ReactNode }) {
  const [pipelines, setPipelines] = useState<PipelineConfig[]>([DEFAULT_PIPELINE]);
  const [activePipelineId, setActivePipelineId] = useState(DEFAULT_PIPELINE.id);

  const activePipeline = pipelines.find((p) => p.id === activePipelineId) || pipelines[0];

  function updatePipeline(fn: (p: PipelineConfig) => PipelineConfig) {
    setPipelines((prev) =>
      prev.map((p) => (p.id === activePipelineId ? fn(p) : p))
    );
  }

  function addStage(name: string, type: StageConfig["type"]) {
    updatePipeline((p) => {
      const maxOrder = Math.max(...p.stages.map((s) => s.order), -1);
      const colorIdx = p.stages.length % STAGE_COLOR_POOL.length;
      const newStage: StageConfig = {
        id: `s-${Date.now()}`,
        name,
        type,
        order: type === "won" ? maxOrder : type === "lost" ? maxOrder + 1 : maxOrder,
        archived: false,
        color: STAGE_COLOR_POOL[colorIdx],
      };
      const stages = [...p.stages, newStage].sort((a, b) => {
        const typeOrder = { active: 0, won: 1, lost: 2 };
        if (typeOrder[a.type] !== typeOrder[b.type]) return typeOrder[a.type] - typeOrder[b.type];
        return a.order - b.order;
      });
      return { ...p, stages };
    });
  }

  function renameStage(stageId: string, newName: string) {
    updatePipeline((p) => ({
      ...p,
      stages: p.stages.map((s) => (s.id === stageId ? { ...s, name: newName } : s)),
    }));
  }

  function reorderStages(stageIds: string[]) {
    updatePipeline((p) => ({
      ...p,
      stages: p.stages.map((s) => {
        const idx = stageIds.indexOf(s.id);
        return idx >= 0 ? { ...s, order: idx } : s;
      }).sort((a, b) => a.order - b.order),
    }));
  }

  function archiveStage(stageId: string) {
    updatePipeline((p) => ({
      ...p,
      stages: p.stages.map((s) => (s.id === stageId ? { ...s, archived: true } : s)),
    }));
  }

  function restoreStage(stageId: string) {
    updatePipeline((p) => ({
      ...p,
      stages: p.stages.map((s) => (s.id === stageId ? { ...s, archived: false } : s)),
    }));
  }

  function getVisibleStages() {
    return activePipeline.stages.filter((s) => !s.archived).sort((a, b) => a.order - b.order);
  }

  function getStageColor(stageName: string) {
    const stage = activePipeline.stages.find((s) => s.name === stageName);
    return stage?.color || "bg-muted/15 text-muted border-muted/30";
  }

  return (
    <PipelineConfigContext.Provider
      value={{
        pipelines,
        activePipeline,
        setActivePipelineId,
        addStage,
        renameStage,
        reorderStages,
        archiveStage,
        restoreStage,
        getVisibleStages,
        getStageColor,
      }}
    >
      {children}
    </PipelineConfigContext.Provider>
  );
}

export function usePipelineConfig() {
  const ctx = useContext(PipelineConfigContext);
  if (!ctx) throw new Error("usePipelineConfig must be used within PipelineConfigProvider");
  return ctx;
}
